import React, { useState } from "react";
import {
  Box,
  Typography,
  Grid,
  Chip,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Pagination,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
} from "@mui/material";
import { FileDownload, CloudUpload } from "@mui/icons-material";
import {
  useGetAllTss,
  useGetTssById,
  useGetAllTssExports,
} from "../../../../../stores/Taxation/taxation.queries";
import { useTriggerExport, useDownloadExport } from "../../../../../stores/Taxation/taxation.mutations";

const ExportSettings: React.FC = () => {
  const [selectedTssId, setSelectedTssId] = useState<string>("");
  const [limit, setLimit] = useState<number>(10); // Items per page
  const [offset, setOffset] = useState<number>(0); // Current offset
  const [maxKnownPage, setMaxKnownPage] = useState<number>(1); // Track highest page with data
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [formData, setFormData] = useState({
    tssId: "",
    startDate: "",
    endDate: "",
    startTransactionNumber: "",
    endTransactionNumber: "",
    startSignatureCounter: "",
    endSignatureCounter: "",
  });

  // Mutation for triggering export
  const triggerExportMutation = useTriggerExport();
  const downloadExportMutation = useDownloadExport();

  // Fetch all TSS for dropdown
  const { data: tssList = [] } = useGetAllTss();

  // Fetch TSS details when selected
  const { data: tssDetails } = useGetTssById(selectedTssId || "");

  // Fetch exports (only when TSS is selected)
  const { data, isLoading, error } = useGetAllTssExports(
    selectedTssId || "",
    limit,
    offset,
  );

  const hasMore = data?.pagination?.hasMore;
  const exports = data?.data || [];

  // Calculate current page and total pages
  const currentPage = Math.floor(offset / limit) + 1;

  // Update max known page based on current data
  const currentMaxKnownPage =
    exports.length > 0
      ? Math.max(maxKnownPage, currentPage)
      : currentPage > 1 && exports.length === 0
        ? currentPage - 1
        : maxKnownPage;

  // Calculate the maximum page count we know about
  const pageCount =
    exports.length > 0 && hasMore
      ? Math.max(currentPage + 1, currentMaxKnownPage)
      : currentMaxKnownPage;

  // Handle page change
  const handlePageChange = (
    event: React.ChangeEvent<unknown>,
    page: number,
  ) => {
    const newOffset = (page - 1) * limit;
    setOffset(newOffset);

    // Update max known page if we're navigating to a page we haven't seen
    if (page > maxKnownPage && exports.length > 0) {
      setMaxKnownPage(page);
    }
  };

  // Handle TSS selection change
  const handleTssChange = (tssId: string) => {
    setSelectedTssId(tssId);
    setOffset(0);
    setMaxKnownPage(1);
  };

  // Handle modal open
  const handleOpenModal = () => {
    setIsModalOpen(true);
    // Pre-fill TSS ID if one is selected
    if (selectedTssId) {
      setFormData((prev) => ({ ...prev, tssId: selectedTssId }));
    }
  };

  // Handle modal close
  const handleCloseModal = () => {
    setIsModalOpen(false);
    setFormData({
      tssId: "",
      startDate: "",
      endDate: "",
      startTransactionNumber: "",
      endTransactionNumber: "",
      startSignatureCounter: "",
      endSignatureCounter: "",
    });
  };

  // Handle input change
  const handleInputChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  // Handle form submit
  const handleFormSubmit = async () => {
    try {
      const exportDto: any = {
        tssId: formData.tssId,
      };

      // Add optional fields only if they have values
      if (formData.startDate) exportDto.startDate = formData.startDate;
      if (formData.endDate) exportDto.endDate = formData.endDate;
      if (formData.startTransactionNumber)
        exportDto.startTransactionNumber = formData.startTransactionNumber;
      if (formData.endTransactionNumber)
        exportDto.endTransactionNumber = formData.endTransactionNumber;
      if (formData.startSignatureCounter)
        exportDto.startSignatureCounter = parseInt(
          formData.startSignatureCounter,
        );
      if (formData.endSignatureCounter)
        exportDto.endSignatureCounter = parseInt(formData.endSignatureCounter);

      await triggerExportMutation.mutateAsync(exportDto);
      handleCloseModal();
    } catch (error) {
      console.error("Error triggering export:", error);
    }
  };

  // Handle download export
  const handleDownloadExport = async (tssId: string, exportId: string) => {
    try {
      const blob = await downloadExportMutation.mutateAsync({ tssId, exportId });
      
      // Create blob URL and trigger download
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `export-${tssId}-${exportId}.tar`;
      link.style.display = "none";
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Clean up blob URL
      setTimeout(() => {
        window.URL.revokeObjectURL(url);
      }, 1000);
    } catch (error) {
      console.error("Error downloading export:", error);
    }
  };

  const getStatusChip = (status: string) => {
    const statusConfig: Record<
      string,
      { label: string; bgcolor: string; color: string }
    > = {
      COMPLETED: {
        label: "Abgeschlossen",
        bgcolor: "#4caf50",
        color: "#fff",
      },
      PENDING: {
        label: "Ausstehend",
        bgcolor: "#ff9800",
        color: "#fff",
      },
      WORKING: {
        label: "In Bearbeitung",
        bgcolor: "#2196f3",
        color: "#fff",
      },
      CANCELLED: {
        label: "Abgebrochen",
        bgcolor: "#9e9e9e",
        color: "#fff",
      },
      ERROR: {
        label: "Fehler",
        bgcolor: "#f44336",
        color: "#fff",
      },
    };

    const config = statusConfig[status] || {
      label: status,
      bgcolor: "#9e9e9e",
      color: "#fff",
    };

    return (
      <Chip
        label={config.label}
        size="small"
        sx={{
          bgcolor: config.bgcolor,
          color: config.color,
          fontWeight: 600,
        }}
      />
    );
  };

  return (
    <Box sx={{ height: "100%", overflow: "auto", pb: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            position: "relative",
          }}
        >
          <Box sx={{ textAlign: "center" }}>
            <Typography
              variant="h4"
              sx={{
                mb: 1,
                fontWeight: 600,
                background: "#003059",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}
            >
              Export Verwaltung
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Verwalten Sie Ihre TSS-Exporte
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<CloudUpload />}
            onClick={handleOpenModal}
            sx={{
              bgcolor: "#003059",
              "&:hover": { bgcolor: "#00509e" },
              color: "white",
              position: "absolute",
              right: 0,
            }}
          >
            Export auslösen
          </Button>
        </Box>
      </Box>

      <Grid container spacing={3}>
        {/* Main Content */}
        <Grid item xs={12}>
          {/* Action Bar */}
          <Paper
            sx={{
              p: 2,
              mb: 2,
              background: "#003059",
              color: "white",
              borderRadius: 1.5,
            }}
          >
            <FormControl
              fullWidth
              size="small"
              sx={{
                "& .MuiOutlinedInput-root": {
                  bgcolor: "white",
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#00509e",
                  },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#003059",
                  },
                },
                "& .MuiInputLabel-root": {
                  color: "rgba(0,0,0,0.6)",
                  "&.Mui-focused": {
                    color: "#003059",
                  },
                },
              }}
            >
              <InputLabel>TSS auswählen</InputLabel>
              <Select
                value={selectedTssId}
                label="TSS auswählen"
                onChange={(e) => handleTssChange(e.target.value)}
              >
                <MenuItem value="">
                  <em>Bitte wählen</em>
                </MenuItem>
                {tssList.map((tss) => (
                  <MenuItem key={tss._id} value={tss._id}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        width: "100%",
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: 600, flex: 1 }}
                      >
                        {tss._id.substring(0, 8)}...
                      </Typography>
                      <Chip
                        label={tss.state}
                        size="small"
                        sx={{
                          bgcolor:
                            tss.state === "INITIALIZED" ? "#4caf50" : "#ff9800",
                          color: "white",
                          height: 20,
                          fontSize: "0.7rem",
                        }}
                      />
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Paper>

          {/* TSS Info Card */}
          {tssDetails && (
            <Paper
              sx={{ p: 1.5, mb: 2, bgcolor: "#f5f7fa", borderRadius: 1.5 }}
            >
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" sx={{ mb: 0.5 }}>
                    <strong>TSS ID:</strong> {tssDetails._id}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Transaktionen:</strong>{" "}
                    {tssDetails.transaction_counter || "0"}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" sx={{ mb: 0.5 }}>
                    <strong>Business:</strong>{" "}
                    {tssDetails.metadata?.business || "N/A"}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Location:</strong>{" "}
                    {tssDetails.metadata?.location || "N/A"}
                  </Typography>
                </Grid>
              </Grid>
            </Paper>
          )}

          {/* Export List */}
          {!selectedTssId ? (
            <Paper sx={{ p: 6, textAlign: "center" }}>
              <FileDownload
                sx={{ fontSize: 64, color: "text.disabled", mb: 2 }}
              />
              <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
                Kein TSS ausgewählt
              </Typography>
              <Typography variant="body2" color="text.disabled">
                Bitte wählen Sie ein TSS aus, um Exporte anzuzeigen
              </Typography>
            </Paper>
          ) : error ? (
            <Alert severity="error" sx={{ mb: 3 }}>
              Fehler beim Laden der Exporte
            </Alert>
          ) : isLoading ? (
            <Box sx={{ textAlign: "center", py: 8 }}>
              <CircularProgress size={48} />
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                Lade Exporte...
              </Typography>
            </Box>
          ) : selectedTssId ? (
            <>
              {exports.length > 0 ? (
                <TableContainer
                  component={Paper}
                  sx={{
                    maxHeight: "500px",
                    mb: 2,
                    boxShadow: 2,
                  }}
                >
                  <Table stickyHeader size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell
                          sx={{
                            fontWeight: 600,
                            bgcolor: "#003059",
                            color: "white",
                          }}
                        >
                          Export ID
                        </TableCell>
                        <TableCell
                          sx={{
                            fontWeight: 600,
                            bgcolor: "#003059",
                            color: "white",
                          }}
                        >
                          Status
                        </TableCell>
                        <TableCell
                          sx={{
                            fontWeight: 600,
                            bgcolor: "#003059",
                            color: "white",
                          }}
                        >
                          Anfragedatum
                        </TableCell>
                        <TableCell
                          sx={{
                            fontWeight: 600,
                            bgcolor: "#003059",
                            color: "white",
                          }}
                        >
                          Startdatum
                        </TableCell>
                        <TableCell
                          sx={{
                            fontWeight: 600,
                            bgcolor: "#003059",
                            color: "white",
                          }}
                        >
                          Enddatum
                        </TableCell>
                        <TableCell
                          sx={{
                            fontWeight: 600,
                            bgcolor: "#003059",
                            color: "white",
                          }}
                        >
                          Aktionen
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {exports.map((exportItem) => (
                        <TableRow
                          key={exportItem._id}
                          sx={{
                            "&:nth-of-type(odd)": { bgcolor: "#f5f5f5" },
                            "&:hover": { bgcolor: "#e3f2fd" },
                          }}
                        >
                          <TableCell sx={{ fontSize: "0.875rem" }}>
                            {exportItem._id}
                          </TableCell>
                          <TableCell>
                            {getStatusChip(exportItem.state)}
                          </TableCell>
                          <TableCell sx={{ fontSize: "0.875rem" }}>
                            {exportItem.time_request_formatted || "-"}
                          </TableCell>
                          <TableCell sx={{ fontSize: "0.875rem" }}>
                            {exportItem.time_start_formatted || "-"}
                          </TableCell>
                          <TableCell sx={{ fontSize: "0.875rem" }}>
                            {exportItem.time_end_formatted || "-"}
                          </TableCell>
                          <TableCell>
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={<FileDownload />}
                              onClick={() =>
                                handleDownloadExport(
                                  selectedTssId,
                                  exportItem._id
                                )
                              }
                              disabled={
                                exportItem.state !== "COMPLETED" ||
                                downloadExportMutation.isPending
                              }
                              sx={{
                                borderColor: "#003059",
                                color: "#003059",
                                "&:hover": {
                                  borderColor: "#00509e",
                                  bgcolor: "rgba(0, 48, 89, 0.04)",
                                },
                              }}
                            >
                              Download
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Paper sx={{ p: 6, textAlign: "center", mb: 2 }}>
                  <FileDownload
                    sx={{ fontSize: 64, color: "text.disabled", mb: 2 }}
                  />
                  <Typography
                    variant="h6"
                    color="text.secondary"
                    sx={{ mb: 1 }}
                  >
                    Keine Exporte auf dieser Seite
                  </Typography>
                  <Typography variant="body2" color="text.disabled">
                    Bitte navigieren Sie zu einer anderen Seite
                  </Typography>
                </Paper>
              )}

              {/* Pagination Controls */}
              {currentPage > 1 || exports.length > 0 ? (
                <Box
                  sx={{
                    mt: 2,
                    mb: 2,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: 2,
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    {exports.length} Exporte angezeigt (Seite {currentPage})
                  </Typography>

                  <Pagination
                    count={pageCount}
                    page={currentPage}
                    onChange={handlePageChange}
                    color="primary"
                    showFirstButton
                    showLastButton
                    disabled={isLoading}
                    sx={{
                      "& .MuiPaginationItem-root": {
                        color: "#1d242b",
                      },
                      "& .Mui-selected": {
                        bgcolor: "#003059 !important",
                        color: "white",
                      },
                    }}
                  />
                </Box>
              ) : null}
            </>
          ) : null}
        </Grid>
      </Grid>

      {/* Export Trigger Modal */}
      <Dialog
        open={isModalOpen}
        onClose={handleCloseModal}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Export auslösen</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Grid container spacing={2}>
              {/* TSS ID Selector */}
              <Grid item xs={12}>
                <FormControl fullWidth required>
                  <InputLabel>TSS auswählen</InputLabel>
                  <Select
                    value={formData.tssId}
                    label="TSS auswählen"
                    onChange={(e) => handleInputChange("tssId", e.target.value)}
                  >
                    <MenuItem value="">
                      <em>Bitte wählen</em>
                    </MenuItem>
                    {tssList.map((tss) => (
                      <MenuItem key={tss._id} value={tss._id}>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            width: "100%",
                          }}
                        >
                          <Typography
                            variant="body2"
                            sx={{ fontWeight: 600, flex: 1 }}
                          >
                            {tss._id.substring(0, 16)}...
                          </Typography>
                          <Chip
                            label={tss.state}
                            size="small"
                            sx={{
                              bgcolor:
                                tss.state === "INITIALIZED"
                                  ? "#4caf50"
                                  : "#ff9800",
                              color: "white",
                              height: 20,
                              fontSize: "0.7rem",
                            }}
                          />
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {/* Date Range */}
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Startdatum"
                  type="date"
                  value={formData.startDate}
                  onChange={(e) =>
                    handleInputChange("startDate", e.target.value)
                  }
                  InputLabelProps={{ shrink: true }}
                  helperText="Optional"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Enddatum"
                  type="date"
                  value={formData.endDate}
                  onChange={(e) => handleInputChange("endDate", e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  helperText="Optional"
                />
              </Grid>

              {/* Transaction Number Range */}
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Start Transaktionsnummer"
                  value={formData.startTransactionNumber}
                  onChange={(e) =>
                    handleInputChange("startTransactionNumber", e.target.value)
                  }
                  helperText="Optional"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="End Transaktionsnummer"
                  value={formData.endTransactionNumber}
                  onChange={(e) =>
                    handleInputChange("endTransactionNumber", e.target.value)
                  }
                  helperText="Optional"
                />
              </Grid>

              {/* Signature Counter Range */}
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Start Signaturzähler"
                  type="number"
                  value={formData.startSignatureCounter}
                  onChange={(e) =>
                    handleInputChange("startSignatureCounter", e.target.value)
                  }
                  helperText="Optional"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="End Signaturzähler"
                  type="number"
                  value={formData.endSignatureCounter}
                  onChange={(e) =>
                    handleInputChange("endSignatureCounter", e.target.value)
                  }
                  helperText="Optional"
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseModal}>Abbrechen</Button>
          <Button
            onClick={handleFormSubmit}
            variant="contained"
            disabled={!formData.tssId || triggerExportMutation.isPending}
            sx={{
              bgcolor: "#003059",
              "&:hover": { bgcolor: "#00509e" },
            }}
          >
            {triggerExportMutation.isPending ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              "Export starten"
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ExportSettings;
